import { NodeViewWrapper, type ReactNodeViewProps } from "@tiptap/react";
import React from "react";
import { formattersFor, keepFormattersFor } from "../../../../utils/formatterMenus";
import { preserveSelection } from "../../../form/RichTextEditor/RichTextEditor";
import { AttributeChip } from "../AttributeChip";
import { AttributePopover } from "../AttributePopover";
import { AttributePopoverAnchor } from "../AttributePopoverAnchor";
import { describeChip } from "../chipDisplay";
import { useAttributeTokenContext } from "./AttributeTokenContext";

/**
 * Inline node view for an `attributeToken` (ENG-591).
 *
 * Renders the shared `AttributeChip` inside the editor content. Clicking it
 * opens the shared `AttributePopover` to re-point the token at a different
 * attribute, give it a short label, or set its formatters; the node stores
 * `attributeKey`, `formatters` and `customLabel`.
 */
export const AttributeTokenView = ({ editor, node, selected, updateAttributes, deleteNode }: ReactNodeViewProps) => {
  const { attributes, groupOrder, formatterOptionsFor, allowCustomLabel } = useAttributeTokenContext();
  const [open, setOpen] = React.useState(false);

  const key = String(node.attrs.attributeKey ?? "");
  const formatters: string[] = Array.isArray(node.attrs.formatters) ? node.attrs.formatters : [];
  const customLabel = String(node.attrs.customLabel ?? "");
  const migratedFrom = String(node.attrs.migratedFrom ?? "");
  const editable = editor.isEditable;
  const chip = describeChip({
    attributes,
    attributeKey: key,
    formatters,
    customLabel,
    migratedFrom,
    editable,
    formatterOptionsFor,
  });
  const { attribute, attributeLabel: label, aliased, formatterOptions } = chip;

  const resolveFormatters = (target: NonNullable<typeof attribute>) =>
    formatterOptionsFor?.(target) ?? formattersFor(target.type);

  return (
    <NodeViewWrapper
      as="span"
      style={{ display: "inline-block" }}
    >
      <AttributePopoverAnchor
        open={open}
        onOpenChange={setOpen}
        // Back to the text, not the chip, so the author can carry on typing.
        returnFocus={() => editor.commands.focus()}
        anchor={
          <AttributeChip
            label={chip.label}
            title={chip.title}
            formatterLabel={chip.formatterLabel}
            aliased={aliased}
            unknown={chip.unknown}
            warning={chip.warning}
            active={selected || open}
            onMouseDown={preserveSelection}
            onClick={editable ? () => setOpen(true) : undefined}
            onRemove={editable ? deleteNode : undefined}
          />
        }
      >
        <AttributePopover
          attributes={attributes}
          groupOrder={groupOrder}
          selectedKey={attribute ? key : undefined}
          onPick={(picked) => {
            // A short label was written for the old attribute, so it doesn't carry over.
            // Re-pointing replaces the migrated reference, so it's no longer flagged.
            updateAttributes({
              attributeKey: picked.key,
              formatters: keepFormattersFor(formatters, resolveFormatters(picked)),
              customLabel: "",
              migratedFrom: "",
            });
            setOpen(false);
          }}
          // An existing short label stays editable (and clearable) even when new ones aren't offered.
          showCustomLabel={allowCustomLabel || aliased}
          customLabelValue={customLabel}
          customLabelPlaceholder={label}
          onCustomLabelChange={(next) => updateAttributes({ customLabel: next })}
          onCustomLabelClear={() => updateAttributes({ customLabel: "" })}
          showFormatter
          formatterOptions={formatterOptions}
          formatters={formatters}
          onFormattersChange={(next) => updateAttributes({ formatters: next })}
        />
      </AttributePopoverAnchor>
    </NodeViewWrapper>
  );
};
